import { useState, useCallback } from 'react';
import {
  addFavorite,
  removeFavorite,
  getFavoriteStatus,
  getUserFavorites,
  checkFavorites,
  FavoriteStatus,
  UserFavoritesResponse,
} from '@/lib/favorites';

interface FavoriteState {
  // Map of "contract:tokenId" -> { isFavorited, favoritesCount }
  statuses: Map<string, FavoriteStatus>;
  // Loading states per NFT
  loading: Set<string>;
  // User's favorites list
  userFavorites: UserFavoritesResponse | null;
  userFavoritesLoading: boolean;
}

export function useFavorites() {
  const [state, setState] = useState<FavoriteState>({
    statuses: new Map(),
    loading: new Set(),
    userFavorites: null,
    userFavoritesLoading: false,
  });

  const getKey = (contract: string, tokenId: string) => `${contract}:${tokenId}`;

  /**
   * Toggle favorite status for an NFT
   */
  const toggleFavorite = useCallback(
    async (
      contract: string,
      tokenId: string,
      isAuthenticated: boolean,
      onAuthRequired: () => void
    ) => {
      if (!isAuthenticated) {
        onAuthRequired();
        return;
      }

      const key = getKey(contract, tokenId);
      const currentStatus = state.statuses.get(key);
      const isFavorited = currentStatus?.isFavorited ?? false;

      // Set loading
      setState((prev) => {
        const newLoading = new Set(prev.loading);
        newLoading.add(key);
        return { ...prev, loading: newLoading };
      });

      try {
        const result = isFavorited
          ? await removeFavorite(contract, tokenId)
          : await addFavorite(contract, tokenId);

        // Update status
        setState((prev) => {
          const newStatuses = new Map(prev.statuses);
          newStatuses.set(key, {
            isFavorited: result.favorited,
            favoritesCount: result.favoritesCount,
          });
          const newLoading = new Set(prev.loading);
          newLoading.delete(key);
          return { ...prev, statuses: newStatuses, loading: newLoading };
        });

        return result;
      } catch (err) {
        // Clear loading on error
        setState((prev) => {
          const newLoading = new Set(prev.loading);
          newLoading.delete(key);
          return { ...prev, loading: newLoading };
        });

        if (err instanceof Error && err.message === 'Authentication required') {
          onAuthRequired();
        }
        throw err;
      }
    },
    [state.statuses]
  );

  /**
   * Load favorite status for a single NFT
   */
  const loadStatus = useCallback(async (contract: string, tokenId: string) => {
    const key = getKey(contract, tokenId);

    try {
      const status = await getFavoriteStatus(contract, tokenId);
      setState((prev) => {
        const newStatuses = new Map(prev.statuses);
        newStatuses.set(key, status);
        return { ...prev, statuses: newStatuses };
      });
      return status;
    } catch {
      return null;
    }
  }, []);

  /**
   * Batch load favorite statuses for multiple NFTs
   */
  const loadStatuses = useCallback(
    async (nfts: Array<{ contract: string; tokenId: string }>) => {
      if (nfts.length === 0) return;

      try {
        // First check which are favorited (requires auth)
        const { favorites } = await checkFavorites(nfts);

        // Then get counts for all (public)
        const statusPromises = nfts.map((nft) =>
          getFavoriteStatus(nft.contract, nft.tokenId)
            .then((status) => ({ key: getKey(nft.contract, nft.tokenId), status }))
            .catch(() => ({ key: getKey(nft.contract, nft.tokenId), status: null }))
        );

        const results = await Promise.all(statusPromises);

        setState((prev) => {
          const newStatuses = new Map(prev.statuses);
          for (const { key, status } of results) {
            if (status) {
              // Merge with favorited status from batch check
              const isFavorited = favorites[key] ?? status.isFavorited;
              newStatuses.set(key, { ...status, isFavorited });
            }
          }
          return { ...prev, statuses: newStatuses };
        });
      } catch {
        // Silently fail - will show 0 counts
      }
    },
    []
  );

  /**
   * Load user's favorites list
   */
  const loadUserFavorites = useCallback(
    async (options: { offset?: number; limit?: number; contract?: string } = {}) => {
      setState((prev) => ({ ...prev, userFavoritesLoading: true }));

      try {
        const result = await getUserFavorites(options);
        setState((prev) => ({ ...prev, userFavorites: result, userFavoritesLoading: false }));
        return result;
      } catch {
        setState((prev) => ({ ...prev, userFavoritesLoading: false }));
        return null;
      }
    },
    []
  );

  /**
   * Get status for an NFT (from cache)
   */
  const getStatus = useCallback(
    (contract: string, tokenId: string): FavoriteStatus | undefined => {
      return state.statuses.get(getKey(contract, tokenId));
    },
    [state.statuses]
  );

  /**
   * Check if an NFT is loading
   */
  const isLoading = useCallback(
    (contract: string, tokenId: string): boolean => {
      return state.loading.has(getKey(contract, tokenId));
    },
    [state.loading]
  );

  /**
   * Update local status (useful for optimistic updates from API response)
   */
  const updateStatus = useCallback(
    (contract: string, tokenId: string, status: Partial<FavoriteStatus>) => {
      const key = getKey(contract, tokenId);
      setState((prev) => {
        const newStatuses = new Map(prev.statuses);
        const existing = newStatuses.get(key) || { isFavorited: false, favoritesCount: 0 };
        newStatuses.set(key, { ...existing, ...status });
        return { ...prev, statuses: newStatuses };
      });
    },
    []
  );

  return {
    toggleFavorite,
    loadStatus,
    loadStatuses,
    loadUserFavorites,
    getStatus,
    isLoading,
    updateStatus,
    userFavorites: state.userFavorites,
    userFavoritesLoading: state.userFavoritesLoading,
  };
}
